import { RESULTS_FEED_NOC_ALIASES } from "./public/results-feed";

/**
 * Historical strength per sport for a NOC, from the last three SEA Games
 * (`packages/contracts/data/medal-history.json`, built by
 * `scripts/build-medal-history.mjs` from Wikipedia medal tables). Admin-only
 * context on the NOC entry editors, and the input of the entries AI analysis
 * (`entries-analysis.ts`): history, never a prediction, never a rule.
 *
 * This module is pure and holds NO data, so it sits in the contracts index:
 * the GMS loads the JSON (`@seagames/contracts/medal-history.json`) with a
 * dynamic `import()` in the admin branch only, so a NOC session never
 * downloads it; the API imports it in the analysis service. Callers pass the
 * dataset in. Its sources and notes are never shown (user, 2026-10-30).
 */

export interface MedalCount {
	g: number;
	s: number;
	b: number;
}

export interface MedalHistoryGames {
	year: number;
	label: string;
	contested: boolean;
	source_urls: string[];
	notes: string | null;
	/** null: not contested, or contested with no medal table read (`missing`). */
	medals: Record<string, MedalCount> | null;
}

export interface MedalHistoryData {
	retrieved: string;
	nocs: string[];
	games: Array<{ year: number; label: string; retrieved: string; source_url: string; notes: string | null }>;
	sports: Record<string, { name: string; games: MedalHistoryGames[]; missing: Array<{ year: number; reason: string }> }>;
}

/** TOP: the sport's best NOC over the three Games (with ≥ 1 gold); GOLD / MEDAL: won one; NONE: took part, no medal; NO_DATA: no numbers. */
export type MedalTier = "TOP" | "GOLD" | "MEDAL" | "NONE" | "NO_DATA";

export interface SportHistory {
	tier: MedalTier;
	totals: MedalCount;
	perGames: Array<MedalCount & { year: number; contested: boolean; hasData: boolean; source_urls: string[]; notes: string | null }>;
	/** Games (with a medal table) in which the NOC won at least one medal. */
	medalledIn: number;
	/** Games in which the sport was contested AND a medal table was read. */
	contestedIn: number;
	/** Competition rank (1, 1, 3) among every NOC by gold, then total medals; null with no data. */
	rank: number | null;
}

const ZERO: MedalCount = { g: 0, s: 0, b: 0 };
const add = (a: MedalCount, b: MedalCount): MedalCount => ({ g: a.g + b.g, s: a.s + b.s, b: a.b + b.b });
const total = (m: MedalCount) => m.g + m.s + m.b;

export function sportHistory(data: MedalHistoryData, sportCode: string, noc: string): SportHistory {
	const sport = data.sports[sportCode];
	const games = sport?.games ?? [];
	const counted = games.filter((g) => g.contested && g.medals !== null);
	const perGames = [...games]
		.sort((a, b) => a.year - b.year)
		.map((g) => ({
			year: g.year,
			contested: g.contested,
			hasData: g.medals !== null,
			source_urls: g.source_urls,
			notes: g.notes,
			...(g.medals?.[noc] ?? ZERO),
		}));
	if (counted.length === 0) return { tier: "NO_DATA", totals: ZERO, perGames, medalledIn: 0, contestedIn: 0, rank: null };

	const sum = (code: string) => counted.reduce((acc, g) => add(acc, g.medals?.[code] ?? ZERO), ZERO);
	const totals = sum(noc);
	const nocs = new Set([...data.nocs, noc, ...counted.flatMap((g) => Object.keys(g.medals ?? {}))]);
	// Ahead = more golds, or as many golds and more medals; ties share the rank.
	const rank =
		1 +
		[...nocs].filter((code) => {
			if (code === noc) return false;
			const o = sum(code);
			return o.g > totals.g || (o.g === totals.g && total(o) > total(totals));
		}).length;
	const medalledIn = counted.filter((g) => total(g.medals?.[noc] ?? ZERO) > 0).length;
	const tier: MedalTier = totals.g > 0 ? (rank === 1 ? "TOP" : "GOLD") : total(totals) > 0 ? "MEDAL" : "NONE";
	return { tier, totals, perGames, medalledIn, contestedIn: counted.length, rank };
}

export interface StrongSport {
	code: string;
	tier: "TOP" | "GOLD";
	gold: number;
	medals: number;
}

/** The sports a NOC won gold in (TOP / GOLD) that it has not picked — most golds first. */
export function strongNotPicked(data: MedalHistoryData, noc: string, pickedCodes: Iterable<string>, among?: Iterable<string>): StrongSport[] {
	const picked = new Set(pickedCodes);
	const codes = among ? [...among] : Object.keys(data.sports);
	const out: StrongSport[] = [];
	for (const code of codes) {
		if (picked.has(code)) continue;
		const h = sportHistory(data, code, noc);
		if (h.tier === "TOP" || h.tier === "GOLD") out.push({ code, tier: h.tier, gold: h.totals.g, medals: total(h.totals) });
	}
	return out.sort((a, b) => b.gold - a.gold || b.medals - a.medals || a.code.localeCompare(b.code));
}

/** Stored organisation code → the dataset's NOC code (the portal's: stored SIN is SGP). */
export const medalHistoryNoc = (code: string | null | undefined): string | null => (code ? (RESULTS_FEED_NOC_ALIASES[code] ?? code) : null);
